import { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp, Minus } from 'lucide-react';
import AbbrTip from '@/components/AbbrTip';
import SectorPeerComparisonModal from '@/components/SectorPeerComparisonModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ValuationMultiplesCard — Round 95d (Jun 2026).
 *
 * 4-tile valuation row for the Stock Metrics tab:
 *   • Trailing P/E   (Massive, Yahoo fallback)
 *   • Forward P/E    (Yahoo only — Massive doesn't ship forward estimates)
 *   • P/S            (Massive, Yahoo fallback)
 *   • PEG            (Massive-derived from P/E ÷ EPS-5yr-CAGR, Yahoo fallback)
 *
 * Each tile shows a "vs Sector Median" delta (computed on-the-fly from
 * `stock_cache` by sector) so users see whether the stock is rich or
 * cheap relative to its peer group — far more useful than the absolute
 * number alone. Sector median uses MEDIAN (not mean) to ignore outliers.
 *
 * Round 96x (Jul 2026) — composite "Framework Verdict" ribbon added
 * at the top for non-dividend equities. Aggregates:
 *   • Multiples cheapness  → PEG bucket (already on the card)
 *   • Rule of 40           → op / fcf raw from equity_quality
 *   • FCF quality          → fcf_margin_pct
 *   • Balance-sheet health → altman_zone
 * Four sub-signals become a single-line verdict pill so a reader can
 * see the framework consensus without opening 3 other tabs.
 *
 * Self-hides for ETFs/funds where the sector is null/Diversified.
 */

const fmtMultiple = (v) => (v == null ? '—' : Number(v).toFixed(2));

function pegTone(v) {
  if (v == null) return 'text-foreground';
  if (v <= 1.0) return 'text-emerald-600 dark:text-emerald-400';
  if (v <= 2.0) return 'text-amber-600 dark:text-amber-400';
  return 'text-rose-600 dark:text-rose-400';
}

function deltaTone(stockVal, sectorVal, lowerIsBetter = true) {
  if (stockVal == null || sectorVal == null || sectorVal <= 0) return null;
  const pct = ((stockVal - sectorVal) / sectorVal) * 100;
  const ratio = stockVal / sectorVal;
  const cheaper = lowerIsBetter ? pct < -5 : pct > 5;
  const richer = lowerIsBetter ? pct > 5 : pct < -5;
  // Round 102i (Feb 2026) — outlier badge threshold. Beyond ±30% we
  // upgrade the chip to a bold "extreme" style with a bg tint; beyond
  // 2× (100%+ above) we switch to multiplier notation ("↑ 3.4× sector")
  // since "+240%" is harder to read at a glance. Below-median stays
  // in percentage form ("↓ 42% below sector") because ratios like
  // 0.58× are less intuitive than "42% cheaper".
  const isExtreme = Math.abs(pct) > 30;
  return {
    pct,
    ratio,
    isExtreme,
    tone: cheaper ? 'text-emerald-600 dark:text-emerald-400'
      : richer ? 'text-rose-600 dark:text-rose-400'
      : 'text-muted-foreground',
    bg: isExtreme
      ? (cheaper ? 'bg-emerald-500/10 ring-1 ring-emerald-500/40 px-1 rounded'
        : richer ? 'bg-rose-500/10 ring-1 ring-rose-500/40 px-1 rounded'
        : '')
      : '',
  };
}

function Tile({ label, abbr, value, sectorVal, tone, lowerIsBetter = true, sectorLabel, testId, onClickOutlier, metricKey }) {
  const delta = deltaTone(value, sectorVal, lowerIsBetter);
  // Round 102k (Feb 2026) — outlier chips are clickable when the
  // parent provides `onClickOutlier`. Clicking opens the Peer
  // Comparison mini-modal listing the 5 cheapest same-sector peers
  // for this metric. We only wire it up for actual outliers (>5%
  // deviation) — a neutral chip with no color has nothing to
  // explore.
  const chipIsClickable = !!onClickOutlier && !!delta && Math.abs(delta.pct) > 5;
  // Sep 2026 (user request) — every tile with a sector delta renders the
  // descriptive sentence as a colored card (green = cheaper than peers,
  // red = richer, muted = in line), not only the extreme (>30%) ones.
  // Hover text alone gave phone users nothing before tapping.
  const sentence = delta ? (
    Math.abs(delta.pct) <= 5 ? (
      <>Trades in line with the sector median — {delta.ratio.toFixed(2)}× peer level.</>
    ) : delta.pct > 0 ? (
      <>Stock trades at {delta.ratio.toFixed(2)}× sector median — {delta.pct.toFixed(0)}% above peers.{chipIsClickable && <> Click for 5 cheapest peers.</>}</>
    ) : (
      <>Stock trades {Math.abs(delta.pct).toFixed(0)}% below sector median — {delta.ratio.toFixed(2)}× peer level.{chipIsClickable && <> Click for 5 cheapest peers.</>}</>
    )
  ) : null;
  const cardBg = delta
    ? (Math.abs(delta.pct) <= 5
      ? 'bg-muted/40 ring-1 ring-border/60'
      : (delta.pct < 0) === lowerIsBetter
        ? 'bg-emerald-500/10 ring-1 ring-emerald-500/40'
        : 'bg-rose-500/10 ring-1 ring-rose-500/40')
    : '';
  const sentenceCard = delta && (
    <span className={`${delta.tone} ${cardBg} font-semibold text-[11px] leading-snug block w-full py-1.5 px-2 rounded`}>
      {sentence}
    </span>
  );
  const compactChip = delta && (
    <span className={`${delta.tone} font-mono text-[11px] inline-flex items-center gap-0.5`}>
      {delta.pct >= 0 ? <>+{delta.pct.toFixed(0)}%</> : <>{delta.pct.toFixed(0)}%</>}
      {Math.abs(delta.pct) > 5 && (delta.pct < 0 ? <TrendingUp className="w-2.5 h-2.5 ml-0.5" /> : <Minus className="w-2.5 h-2.5 ml-0.5" />)}
    </span>
  );
  return (
    <div className="flex flex-col p-2 rounded-md border bg-card" data-testid={testId}>
      <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
        <AbbrTip term={abbr}>{label}</AbbrTip>
      </div>
      <div className={`font-mono font-bold text-base ${tone || 'text-foreground'}`}>
        {fmtMultiple(value)}
      </div>
      {sectorVal != null && (
        <>
          <div className="text-[10px] font-bold mt-0.5 flex items-center gap-1" title={`Sector median across ${sectorLabel || 'peers'}`}>
            <span className="text-muted-foreground">vs {sectorLabel || 'sector'} {fmtMultiple(sectorVal)}</span>
            {compactChip}
          </div>
          {delta && (
            <div className="mt-1 flex-1 flex items-stretch" data-testid={`${testId}-explainer`}>
              {chipIsClickable ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onClickOutlier(metricKey); }}
                  className="w-full text-left hover:brightness-110 transition-all"
                  data-testid={`${testId}-outlier`}
                  data-extreme={delta.isExtreme ? 'true' : 'false'}
                >
                  {sentenceCard}
                </button>
              ) : sentenceCard}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function ValuationMultiplesCard({ stock, onOpenStock, peersClickable = true }) {
  const [sectorData, setSectorData] = useState(null);
  // Round 96x — equity_quality fields (Rule of 40, FCF margin, Altman zone)
  // for the composite Framework Verdict ribbon. Non-blocking — if the
  // fetch fails the ribbon just doesn't render and the tiles below
  // still work.
  const [eq, setEq] = useState(null);
  // Round 102k (Feb 2026) — Peer Comparison mini-modal state. Set to
  // the metric key (`trailing_pe`, `price_to_sales`, etc.) when the
  // user clicks an outlier chip.
  const [peerModalMetric, setPeerModalMetric] = useState(null);
  const onTileOutlier = peersClickable ? setPeerModalMetric : undefined;

  // Round 95w (Jun 2026) — structural skip for asset classes where
  // P/E-driven multiples are meaningless. Each of these has its own
  // dedicated valuation card elsewhere in the modal, so suppressing
  // the generic multiples row removes "—" tiles + misleading sector
  // deltas (e.g. a REIT's $0 EPS dividing into a 500× P/E vs a real
  // operating-company sector median).
  //
  //   REIT  → REIT Cash Lens (FFO / AFFO / FFO Payout)
  //   BDC   → NII per share + NAV / share (no analyst PEG coverage)
  //   CEF   → Premium/Discount-to-NAV gauge + Z-Score (Phase 5)
  //   MLP   → DCF per unit (distributable cash flow, not earnings)
  //   ETF   → Fund-level P/E is the cap-weighted average of holdings;
  //           comparing to an operating-company sector median is
  //           apples-to-oranges. Round 95z (Jun 2026) adds this row
  //           after SPDV — an S&P 500 dividend ETF — still rendered
  //           the card with a "vs Technology peers" delta.
  //   Preferred Share / Baby Bond → fixed coupon × par, no P/E
  //
  // Common stock and ADRs keep the card.
  const ac = stock?.asset_class;
  const structurallyHidden = (
    stock?.is_reit
    || stock?.is_bdc
    || stock?.is_cef
    || stock?.is_mlp
    || stock?.is_etf
    || ac === 'preferred_share'
    || ac === 'baby_bond'
  );
  // Skip the network call entirely when hidden — keeps the
  // sector-multiples warmer cool and the inspector logs clean.
  const skip = !stock?.ticker || structurallyHidden;

  useEffect(() => {
    if (skip) { setSectorData(null); return undefined; }
    let cancelled = false;
    axios.get(`${API}/stocks/${stock.ticker}/sector-multiples`, { withCredentials: true })
      .then((r) => { if (!cancelled) setSectorData(r.data || null); })
      .catch(() => { if (!cancelled) setSectorData(null); });
    return () => { cancelled = true; };
  }, [stock?.ticker, skip]);

  // Round 96x — equity-quality fetch for the composite verdict ribbon.
  useEffect(() => {
    if (skip) { setEq(null); return undefined; }
    let cancelled = false;
    axios.get(`${API}/stocks/${stock.ticker}/equity-quality`, { withCredentials: true })
      .then((r) => { if (!cancelled) setEq(r.data?.equity_quality || null); })
      .catch(() => { if (!cancelled) setEq(null); });
    return () => { cancelled = true; };
  }, [stock?.ticker, skip]);

  if (structurallyHidden) return null;

  // Round 95d v2 — read both Massive's `trailing_pe` (preferred) and
  // the Yahoo fallback `pe_ratio`. Same idea for forward — only Yahoo
  // ships forwardPE, but field name is `pe_forward` everywhere.
  const pe = stock?.trailing_pe ?? stock?.pe_ratio;
  const pef = stock?.pe_forward ?? stock?.forward_pe;
  const ps = stock?.price_to_sales;
  const pegVendor = stock?.peg_ratio;
  const evEbitda = stock?.enterprise_to_ebitda;

  // Round 96y (Jul 2026) — Fallback ladder so this card renders
  // *something* useful for non-profitable growth names where the
  // vendor P/E and PEG come back null. Two swaps:
  //
  //   • P/E tile: when trailing AND forward P/E are both null,
  //     substitute EV/EBITDA. EV/EBITDA is defined for any operating
  //     business (positive or negative EBITDA — negative reads are
  //     legitimate "distressed" signals). We badge the tile so the
  //     reader knows the substitution happened.
  //
  //   • PEG tile: PEG (P/E ÷ growth) is undefined whenever EPS is
  //     negative. Two fallback formulas:
  //       (a) if trailing P/E and revenue growth are both positive:
  //           synthetic PEG = P/E ÷ revenue-growth-% (uses revenue
  //           growth as the growth proxy when analyst EPS-growth
  //           isn't published) — badge "PEG (rev)"
  //       (b) else if P/S and revenue growth are both positive:
  //           P/S ÷ growth (the "growth-adjusted sales multiple",
  //           standard for pre-profit growth stocks) — badge "P/S/g"
  //
  // Both fallbacks require rev_growth_yoy_pct from equity_quality,
  // which we already fetch above.
  const revGrowth = eq?.rev_growth_yoy_pct ?? null;
  const useEvForPe = pe == null && pef == null && evEbitda != null;
  const peLabel = useEvForPe ? 'EV/EBITDA' : 'Trailing P/E';
  const peAbbr = useEvForPe ? 'EV_EBITDA' : 'TRAILING_PE';
  const peValue = useEvForPe ? evEbitda : pe;
  const peSectorVal = useEvForPe ? null : sectorData?.median_pe;

  let pegFinal = pegVendor;
  let pegBadge = null;
  // Cap the growth input for PEG fallbacks. A revenue spike above
  // 100%/yr almost always reflects a one-off base-effect (micro-cap
  // going from $2M → $12M, or a company recognizing a large one-time
  // contract) rather than a durable growth rate. Using it to compute
  // PEG would make the multiple look absurdly cheap on a growth story
  // that isn't real. When growth is > 100 we skip the fallback and
  // leave PEG as "—" — an honest read.
  const growthUsable = revGrowth != null && revGrowth > 0 && revGrowth <= 100;
  if (pegVendor == null && pe != null && pe > 0 && growthUsable) {
    pegFinal = pe / revGrowth;
    pegBadge = 'PEG (rev)';
  } else if (pegVendor == null && ps != null && ps > 0 && growthUsable) {
    pegFinal = ps / revGrowth;
    pegBadge = 'P/S/g';
  }

  // Hide if literally no valuation data (checked AFTER fallbacks so
  // an unprofitable growth name with EV/EBITDA + P/S still renders).
  if (peValue == null && pef == null && ps == null && pegFinal == null) return null;

  const sectorShort = sectorData?.sector
    ? sectorData.sector.split(' ')[0]   // "Financial Services" → "Financial"
    : 'sector';

  // Round 96x — Framework Verdict composite.
  //
  // Four sub-signals, each mapped to -1 / 0 / +1:
  //   1. Multiples cheapness  → PEG bucket (<=1 cheap = +1, 1-2 fair = 0, >2 rich = -1)
  //   2. Rule of 40           → op or fcf raw (>=40 = +1, 20-40 = 0, <20 = -1)
  //   3. FCF quality          → fcf_margin_pct (>=20 = +1, 10-20 = 0, <10 = -1)
  //   4. Balance-sheet health → altman_zone ("safe" = +1, "gray" = 0, "distress" = -1)
  //
  // Composite sum {-4..+4} maps to a three-bucket verdict:
  //   >=+2  emerald "Compounder — cheap for the growth"
  //   -1..+1 amber  "Fair for the growth"
  //   <=-2  rose    "Rich or risky for the growth"
  //
  // Ribbon self-hides when fewer than 3 of the 4 sub-signals are
  // available (avoids showing a misleading verdict from 1-2 inputs).
  const bucketPeg = pegFinal == null ? null : (pegFinal <= 1.0 ? 1 : pegFinal <= 2.0 ? 0 : -1);
  const r40 = eq?.rule_of_40_op_raw ?? eq?.rule_of_40_fcf_raw ?? null;
  // Cap Rule of 40 above 200 at +1 so a one-off revenue-spike outlier
  // (e.g. a micro-cap that went from $2M → $12M) doesn't dominate the
  // composite as if it were a durable compounder.
  const bucketR40 = r40 == null ? null : (r40 >= 40 ? 1 : r40 >= 20 ? 0 : -1);
  const fcfM = eq?.fcf_margin_pct ?? null;
  const bucketFcf = fcfM == null ? null : (fcfM >= 20 ? 1 : fcfM >= 10 ? 0 : -1);
  // Round 96y bugfix — altman_zone backend enum ships as
  // "Safe" / "Grey" / "Distress" (capitalized). Compare case-insensitively.
  const altmanZ = eq?.altman_zone == null ? null : String(eq.altman_zone).toLowerCase();
  const bucketAltman = altmanZ == null ? null
    : (altmanZ === 'safe' ? 1 : (altmanZ === 'grey' || altmanZ === 'gray') ? 0 : -1);
  const buckets = [bucketPeg, bucketR40, bucketFcf, bucketAltman].filter((x) => x != null);
  const composite = buckets.length >= 3 ? buckets.reduce((s, x) => s + x, 0) : null;
  const verdictTone = composite == null ? null
    : composite >= 2 ? 'text-emerald-600 dark:text-emerald-400 border-emerald-500/40 bg-emerald-500/10'
    : composite <= -2 ? 'text-rose-600 dark:text-rose-400 border-rose-500/40 bg-rose-500/10'
    : 'text-amber-600 dark:text-amber-400 border-amber-500/40 bg-amber-500/10';
  const verdictLabel = composite == null ? null
    : composite >= 2 ? 'Compounder — cheap for the growth'
    : composite <= -2 ? 'Rich or risky for the growth'
    : 'Fair for the growth';

  return (
    <div className="rounded-lg border bg-card p-3" data-testid="valuation-multiples-card">
      {verdictLabel && (
        <div
          className={`mb-2 flex flex-wrap items-center justify-between gap-1.5 rounded-md border px-2.5 py-1.5 text-[11px] font-semibold ${verdictTone}`}
          data-testid="valuation-framework-verdict"
          data-composite={composite}
        >
          <span className="flex items-center gap-1.5">
            <AbbrTip term="VALUATION_FRAMEWORK">Framework Verdict</AbbrTip>
            <span>·</span>
            <span>{verdictLabel}</span>
          </span>
          <span className="flex items-center gap-1 text-[10px] font-mono text-current/80">
            <span title="Price/Earnings-to-Growth" className={bucketPeg === 1 ? 'text-emerald-500' : bucketPeg === -1 ? 'text-rose-500' : bucketPeg === 0 ? 'text-amber-500' : 'text-muted-foreground/40'}>PEG</span>
            <span title="Rule of 40 (revenue growth + margin)" className={bucketR40 === 1 ? 'text-emerald-500' : bucketR40 === -1 ? 'text-rose-500' : bucketR40 === 0 ? 'text-amber-500' : 'text-muted-foreground/40'}>R40</span>
            <span title="Free Cash Flow margin" className={bucketFcf === 1 ? 'text-emerald-500' : bucketFcf === -1 ? 'text-rose-500' : bucketFcf === 0 ? 'text-amber-500' : 'text-muted-foreground/40'}>FCF</span>
            <span title="Altman Z-Score balance-sheet health" className={bucketAltman === 1 ? 'text-emerald-500' : bucketAltman === -1 ? 'text-rose-500' : bucketAltman === 0 ? 'text-amber-500' : 'text-muted-foreground/40'}>ALT</span>
            <span className="ml-1 text-current/60">{composite >= 0 ? `+${composite}` : composite}</span>
          </span>
        </div>
      )}
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <h4 className="text-[11px] font-bold uppercase tracking-widest text-fuchsia-500 dark:text-fuchsia-400 flex items-center gap-1.5 flex-wrap">
          Valuation Multiples
          {/* Round P108ak (Feb 2026) — subtitle styled a notch smaller
              than the title (same pattern as "View Today's" alongside
              the Price Chart heading). */}
          <span className="text-[9px] font-semibold normal-case tracking-normal text-muted-foreground">
            — the whole story at a glance
          </span>
        </h4>
        {sectorData?.count > 0 && (
          <span className="text-[10px] font-bold text-muted-foreground">
            vs {sectorData.count} {sectorData.sector} peers (median)
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2">
        <Tile
          label={peLabel}
          abbr={peAbbr}
          value={peValue}
          sectorVal={peSectorVal}
          sectorLabel={sectorShort}
          testId="valuation-tile-pe"
          onClickOutlier={onTileOutlier}
          metricKey={useEvForPe ? 'enterprise_to_ebitda' : 'trailing_pe'}
        />
        <Tile
          label="Forward P/E"
          abbr="FORWARD_PE"
          value={pef}
          sectorVal={sectorData?.median_pe_forward}
          sectorLabel={sectorShort}
          testId="valuation-tile-pe-forward"
          onClickOutlier={onTileOutlier}
          metricKey="pe_forward"
        />
        <Tile
          label="P/S"
          abbr="P_S"
          value={ps}
          sectorVal={sectorData?.median_ps}
          sectorLabel={sectorShort}
          testId="valuation-tile-ps"
          onClickOutlier={onTileOutlier}
          metricKey="price_to_sales"
        />
        <Tile
          label={pegBadge || 'PEG'}
          abbr="PEG"
          value={pegFinal}
          sectorVal={pegBadge ? null : sectorData?.median_peg}
          sectorLabel={sectorShort}
          tone={pegTone(pegFinal)}
          testId="valuation-tile-peg"
          onClickOutlier={onTileOutlier}
          metricKey="peg_ratio"
        />
        {/* Round 102j (Feb 2026) — 5th tile: EV/EBITDA. Skipped when
            the Trailing P/E tile already shows EV/EBITDA as a fallback
            (useEvForPe=true) to avoid duplicate. Neutralizes capital
            structure differences — growth investors + BDC analysts
            prefer this over raw P/E for cross-comparison. */}
        {!useEvForPe && (
          <Tile
            label="EV/EBITDA"
            abbr="EV_EBITDA"
            value={evEbitda}
            sectorVal={sectorData?.median_ev_ebitda}
            sectorLabel={sectorShort}
            testId="valuation-tile-ev-ebitda"
            onClickOutlier={onTileOutlier}
            metricKey="enterprise_to_ebitda"
          />
        )}
      </div>
      {/* Round 102k (Feb 2026) — Peer Comparison mini-modal. Opens
          when the user clicks an outlier chip. `onOpenStock` is
          plumbed from the parent Stock Modal so clicking a peer
          swaps the modal to that ticker instead of navigating away. */}
      <SectorPeerComparisonModal
        open={!!peerModalMetric}
        onOpenChange={(v) => { if (!v) setPeerModalMetric(null); }}
        ticker={stock?.ticker}
        metric={peerModalMetric}
        sectorLabel={sectorData?.sector}
        currentValue={
          peerModalMetric === 'trailing_pe' ? peValue
          : peerModalMetric === 'pe_forward' ? pef
          : peerModalMetric === 'price_to_sales' ? ps
          : peerModalMetric === 'peg_ratio' ? pegFinal
          : peerModalMetric === 'enterprise_to_ebitda' ? evEbitda
          : null
        }
        onOpenStock={onOpenStock}
      />
      {(useEvForPe || pegBadge) && (
        <p className="text-[10px] text-muted-foreground mt-2" data-testid="valuation-fallback-note">
          {useEvForPe && (
            <span>
              <span className="font-semibold text-foreground/70">EV/EBITDA</span> shown in place of P/E — TTM earnings not positive.{' '}
            </span>
          )}
          {pegBadge === 'PEG (rev)' && (
            <span>
              <span className="font-semibold text-foreground/70">PEG (rev)</span> uses P/E ÷ revenue-growth-% (analyst PEG not published).
            </span>
          )}
          {pegBadge === 'P/S/g' && (
            <span>
              <span className="font-semibold text-foreground/70">P/S/g</span> uses P/S ÷ revenue-growth-% — the growth-adjusted sales multiple for pre-profit growth stocks.
            </span>
          )}
        </p>
      )}
      {pegFinal != null && (
        <p className="text-[10px] font-bold text-muted-foreground mt-2">
          {pegBadge || 'PEG'} colour: <span className="text-emerald-600 dark:text-emerald-400">≤ 1.0 cheap</span> ·
          <span className="text-amber-600 dark:text-amber-400"> 1–2 fair</span> ·
          <span className="text-rose-600 dark:text-rose-400"> &gt; 2 rich</span>.
          Lower = the stock&apos;s price is rising slower than its growth.
        </p>
      )}
    </div>
  );
}
